<section class="landing-pages-shell">
  @if (error) {
    <p class="error">{{ error }}</p>
  }

  @if (sites.length) {
    <cms-landing-page-list
      [landingPages]="landingPages"
      [activeFilters]="tagFilter.tags"
      [busy]="loading"
      (createRequested)="createLandingPage()"
      (importRequested)="openImportDialog()"
      (filterToggled)="toggleTagFilter($event)"
      (clearFiltersRequested)="clearFilters()"
      (landingPageOpened)="openLandingPage($event)" />
  } @else {
    <section class="table-panel">
      <header class="table-panel-header">
        <div>
          <p class="eyebrow">Landing pages</p>
          <h2>No sites available</h2>
        </div>
      </header>
      <p class="empty-state">Create a site first before managing landing pages.</p>
    </section>
  }
</section>

@if (isImportDialogOpen) {
  <div class="dialog-backdrop" (click)="closeImportDialog()">
    <section
      class="dialog-panel import-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="landing-page-import-title"
      (click)="$event.stopPropagation()">
      <header class="dialog-header">
        <div>
          <p class="eyebrow">Import</p>
          <h2 id="landing-page-import-title">Import from Markdown</h2>
          <p class="dialog-copy">Create a structured landing page for {{ selectedSiteName }} from one Markdown source.</p>
        </div>
        <button type="button" class="ghost small" [disabled]="importSaving" (click)="closeImportDialog()">Close</button>
      </header>

      @if (importError) {
        <p class="error">{{ importError }}</p>
      }

      @if (!importPreview) {
        <div
          class="import-dropzone"
          [class.is-dragging]="importDragging"
          (dragover)="onImportDragOver($event)"
          (dragleave)="onImportDragLeave($event)"
          (drop)="onImportDrop($event)">
          <div>
            <p class="import-title">Drop a Markdown file here</p>
            <p class="import-note">You can also choose a file and edit or paste content below before parsing.</p>
          </div>

          <div class="import-file-actions">
            <input #importFileInput type="file" accept=".md,.markdown,text/markdown,text/plain" hidden (change)="onImportFileSelected($event)" />
            <button type="button" class="ghost" (click)="importFileInput.click()">Choose file</button>
            @if (importSourceName) {
              <span class="import-source">{{ importSourceName }}</span>
            }
          </div>
        </div>

        <label class="import-field">
          <span>Markdown content</span>
          <textarea
            rows="14"
            [ngModel]="markdownImportContent"
            (ngModelChange)="markdownImportContent = $event"
            placeholder="Paste Markdown here or load a file to review/edit before parsing."></textarea>
        </label>

        <div class="dialog-actions">
          <button type="button" class="ghost small" [disabled]="importSaving || !markdownImportContent" (click)="resetImportSource()">
            Reset
          </button>
          <button type="button" class="accent" [disabled]="importSaving || !markdownImportContent.trim()" (click)="parseMarkdownImport()">
            Parse Markdown
          </button>
        </div>
      } @else {
        <section class="import-preview">
          <div class="import-summary-grid">
            <div>
              <span>Title</span>
              <strong>{{ importPreview.landingPage.title }}</strong>
            </div>
            <div>
              <span>Path</span>
              <strong><code>{{ importPreview.landingPage.path }}</code></strong>
            </div>
            <div>
              <span>Summary source</span>
              <strong>{{ summarySourceLabel(importPreview.summarySource) }}</strong>
            </div>
            <div>
              <span>Detected state</span>
              <strong>{{ importPreview.detectedPublicationState }}</strong>
            </div>
            <div>
              <span>Date</span>
              <strong>{{ importPreview.frontmatter.date || 'Not found' }}</strong>
            </div>
            <div>
              <span>Will save as</span>
              <strong>{{ effectiveImportState() }}</strong>
            </div>
          </div>

          <div class="import-preview-section">
            <span>Tags</span>
            @if (importPreview.landingPage.tags.length) {
              <div class="content-tags">
                @for (tag of importPreview.landingPage.tags; track tag) {
                  <span class="content-tag">{{ tag }}</span>
                }
              </div>
            } @else {
              <span class="empty-tags">None</span>
            }
          </div>

          <div class="import-preview-section">
            <span>Blocks found</span>
            @if (importPreview.blockCounts.length) {
              <div class="content-tags">
                @for (blockCount of importPreview.blockCounts; track blockCount.type) {
                  <span class="content-tag">{{ blockCount.type }} x{{ blockCount.count }}</span>
                }
              </div>
            } @else {
              <span class="empty-tags">No structured blocks found</span>
            }
          </div>

          <div class="import-preview-section">
            <span>Warnings ({{ importPreview.warnings.length }})</span>
            @if (importPreview.warnings.length) {
              <ul class="warning-list">
                @for (warning of importPreview.warnings; track warning.message) {
                  <li>{{ warning.message }}</li>
                }
              </ul>
            } @else {
              <p class="import-note">No warnings detected.</p>
            }
          </div>

          @if (importPreview.detectedPublicationState !== 'Draft') {
            <label class="draft-toggle">
              <input type="checkbox" [ngModel]="importAsDraft" (ngModelChange)="importAsDraft = $event" />
              <span>Import as draft instead</span>
            </label>
          }
        </section>

        <div class="dialog-actions">
          <button type="button" class="ghost small" [disabled]="importSaving" (click)="returnToImportInput()">Start over</button>
          <button type="button" class="accent" [disabled]="importSaving" (click)="acceptMarkdownImport()">
            {{ importSaving ? 'Importing...' : 'Import landing page' }}
          </button>
        </div>
      }
    </section>
  </div>
}
